import React from 'react';
import {
  Card,
  Typography,
  Button,
  Box,
  Paper,
  Divider,
  Avatar,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import GitHubIcon from '@material-ui/icons/GitHub';
import CloudIcon from '@material-ui/icons/Cloud';
import { configApiRef, useApi } from '@backstage/core-plugin-api';

// Badal.io brand colors
const badalPrimaryBlue = '#1e87f0';
const badalTeal = '#5bbad5';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${badalPrimaryBlue} 0%, ${badalTeal} 100%)`,
  },
  logo: {
    height: 'auto',
    maxHeight: 120,
    width: 'auto',
    maxWidth: '300px',
    margin: `${theme.spacing(4)}px 0`,
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
  },
  card: {
    minWidth: 400,
    maxWidth: 500,
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(4),
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: theme.palette.text.secondary,
  },
  buttonSection: {
    marginTop: theme.spacing(3),
  },
  button: {
    width: '100%',
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 500,
    borderRadius: theme.spacing(1),
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
    },
  },
  githubButton: {
    backgroundColor: '#24292e',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#1a1e22',
    },
  },
  googleButton: {
    backgroundColor: '#4285f4',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#357ae8',
    },
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  icon: {
    marginRight: theme.spacing(1),
  },
  statusSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(1),
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  avatar: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    marginRight: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
  },
  signOutButton: {
    marginTop: theme.spacing(2),
  },
}));

export const LoginPage = () => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  
  // Get configuration values
  const backendUrl = config.getString('backend.baseUrl');
  const authEnvironment = config.getString('auth.environment');
  
  // Get current user info if available
  const [githubUser, setGithubUser] = React.useState<any>(null);
  const [googleUser, setGoogleUser] = React.useState<any>(null);

  React.useEffect(() => {
    // Check for existing sessions
    const checkSessions = async () => {
      try {
        // Check GitHub session
        const githubResponse = await fetch(`${backendUrl}/api/auth/github/refresh?optional&env=${authEnvironment}`, {
          credentials: 'include',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });
        if (githubResponse.ok) {
          const githubData = await githubResponse.json();
          if (githubData.profile) {
            setGithubUser(githubData.profile);
          }
        }
      } catch (error) {
        console.log('No GitHub session');
      }

      try {
        // Check Google session
        const googleResponse = await fetch(`${backendUrl}/api/auth/google/refresh?optional&env=${authEnvironment}`, {
          credentials: 'include',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });
        if (googleResponse.ok) {
          const googleData = await googleResponse.json();
          if (googleData.profile) {
            setGoogleUser(googleData.profile);
          }
        }
      } catch (error) {
        console.log('No Google session');
      }
    };

    checkSessions();
  }, [backendUrl, authEnvironment]);

  const handleGitHubLogin = () => {
    const authWindow = window.open(
      `${backendUrl}/api/auth/github/start?env=${authEnvironment}`,
      'github-auth',
      'width=600,height=700,left=200,top=100'
    );
    
    // Check if auth was successful
    const checkInterval = setInterval(() => {
      if (authWindow?.closed) {
        clearInterval(checkInterval);
        // Refresh the session status
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    }, 500);
  };

  const handleGoogleLogin = () => {
    const authWindow = window.open(
      `${backendUrl}/api/auth/google/start?env=${authEnvironment}`,
      'google-auth',
      'width=600,height=700,left=200,top=100'
    );
    
    // Check if auth was successful
    const checkInterval = setInterval(() => {
      if (authWindow?.closed) {
        clearInterval(checkInterval);
        // Refresh the session status
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    }, 500);
  };

  const handleSignOut = async (provider: string) => {
    try {
      await fetch(`${backendUrl}/api/auth/${provider}/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
      window.location.reload();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  return (
    <Box className={classes.root}>
      <img 
        src="/badal-logo.png" 
        alt="Badal" 
        className={classes.logo}
      />
      <Card className={classes.card}>
        <Box className={classes.header}>
          <Typography variant="body1" className={classes.subtitle}>
            Choose your authentication method to continue
          </Typography>
        </Box>

        <Divider className={classes.divider} />

        {/* Current Sessions */}
        {(githubUser || googleUser) && (
          <>
            <Paper className={classes.statusSection} elevation={0}>
              <Typography variant="h6" gutterBottom>
                Current Sessions
              </Typography>
              
              {githubUser && (
                <Box mb={2}>
                  <Box className={classes.userInfo}>
                    <Avatar className={classes.avatar}>
                      <GitHubIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2">
                        <strong>GitHub:</strong> {githubUser.displayName || githubUser.email}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {githubUser.email}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleSignOut('github')}
                  >
                    Sign Out
                  </Button>
                </Box>
              )}

              {googleUser && (
                <Box>
                  <Box className={classes.userInfo}>
                    <Avatar className={classes.avatar}>
                      <CloudIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2">
                        <strong>Google:</strong> {googleUser.displayName || googleUser.email}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {googleUser.email}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleSignOut('google')}
                  >
                    Sign Out
                  </Button>
                </Box>
              )}
            </Paper>
            <Divider className={classes.divider} />
          </>
        )}

        {/* Sign In Buttons */}
        <Box className={classes.buttonSection}>
          <Typography variant="h6" gutterBottom>
            {githubUser || googleUser ? 'Add Another Account' : 'Sign In With'}
          </Typography>
          
          {!githubUser && (
            <Button
              variant="contained"
              className={`${classes.button} ${classes.githubButton}`}
              onClick={handleGitHubLogin}
              startIcon={<GitHubIcon className={classes.icon} />}
            >
              Sign in with GitHub
            </Button>
          )}

          {!googleUser && (
            <Button
              variant="contained"
              className={`${classes.button} ${classes.googleButton}`}
              onClick={handleGoogleLogin}
              startIcon={<CloudIcon className={classes.icon} />}
            >
              Sign in with Google Cloud
            </Button>
          )}

        </Box>

        <Divider className={classes.divider} />

        <Typography variant="body2" color="textSecondary" align="center">
          You can sign in with multiple accounts to access different resources.
          Your sessions will be maintained separately for each provider.
        </Typography>
      </Card>
    </Box>
  );
};